import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { queueCount } from "@/lib/offlineDb";
import { replayQueue } from "@/lib/api";
import { toast } from "sonner";

const OnlineContext = createContext({ online: true, pending: 0, syncNow: () => {} });

export function OnlineProvider({ children }) {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(async () => {
    try {
      const c = await queueCount();
      setPending(c);
    } catch { /* ignore */ }
  }, []);

  const syncNow = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await replayQueue();
      if (result.successes > 0) {
        toast.success(
          `Sincronizzate ${result.successes} modifiche${
            result.failures > 0 ? ` · ${result.failures} riprovate` : ""
          }`
        );
      }
    } catch (e) {
      toast.error("Sincronizzazione fallita");
    } finally {
      setSyncing(false);
      refreshPending();
    }
  }, [syncing, refreshPending]);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      refreshPending().then(() => syncNow());
    };
    const handleOffline = () => {
      setOnline(false);
      refreshPending();
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    refreshPending();
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refreshPending, syncNow]);

  return (
    <OnlineContext.Provider value={{ online, pending, syncing, syncNow, refreshPending }}>
      {children}
    </OnlineContext.Provider>
  );
}

export function useOnline() {
  return useContext(OnlineContext);
}
